import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

interface WagesOffSheetProps {
  wageSheetData?: any;
}

const toNumber = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPound = (value: any) => {
  const numeric = toNumber(value);
  return Number.isInteger(numeric)
    ? `£${numeric}`
    : `£${numeric.toFixed(2)}`;
};

const formatCompact = (value: any) => {
  const numeric = toNumber(value);
  return Number.isInteger(numeric) ? String(numeric) : String(numeric);
};

const formatTwo = (value: any) => toNumber(value).toFixed(2);

const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatDayLabel = (row: any) => {
  if (row?.rawDate && dayjs(row.rawDate).isValid()) {
    const d = dayjs(row.rawDate);
    const dateWithOrdinal = `${d.date()}${getOrdinalSuffix(d.date())}`;
    return `${d.format("ddd")} ${dateWithOrdinal}\n${d.format("MMM")}`;
  }

  return String(row?.day || "-");
};

const WagesOffSheet = ({ wageSheetData }: WagesOffSheetProps) => {
  const { leadDetails } = useSelector((state: any) => state.lead);

  const defaultOptions = {
    showWage: true,
    showOvertime: true,
    showExtras: true,
    showTaskDetails: true,
  };

  const sheets = Array.isArray(wageSheetData?.sheets)
    ? wageSheetData.sheets
    : wageSheetData
      ? [wageSheetData]
      : [];

  return (
    <>
      <style>
        {`
          .wage-sheet-root {
            max-width: 1480px;
            margin: 0 auto;
            padding: 16px;
            font-family: "Times New Roman", serif;
            font-size: 13px;
          }

          .wage-sheet-scroll {
            width: 100%;
            overflow-x: auto;
          }

          .wage-sheet-scroll table {
            min-width: 1080px;
          }

          .wage-sheet-block {
            margin-bottom: 12px;
            border: 1px solid #6f6f6f;
            display: flex;
            align-items: stretch;
            background: #efefef;
          }

          .wage-sheet-left {
            width: 265px;
            flex: 0 0 265px;
            border-right: 1px solid #6f6f6f;
            background: #efefef;
          }

          .wage-sheet-right {
            flex: 1;
            min-width: 0;
            background: #f3f3f3;
          }

          @media (max-width: 900px) {
            .wage-sheet-block {
              flex-direction: column;
            }

            .wage-sheet-left {
              width: 100%;
              flex: 1;
              border-right: none;
              border-bottom: 1px solid #6f6f6f;
            }
          }

          @media print {
            @page {
              size: A4 landscape;
              margin: 8mm;
            }

            .wage-sheet-root {
              max-width: none;
              margin: 0;
              padding: 0;
            }

            .wage-sheet-scroll {
              overflow: visible;
            }

            .wage-sheet-scroll table {
              min-width: auto;
              width: 100%;
            }

            .wage-sheet-scroll .print-wage-table {
              border: 0 !important;
            }

            .wage-sheet-scroll .print-wage-table th,
            .wage-sheet-scroll .print-wage-table td {
              border: 1px solid #777 !important;
            }

            .wage-sheet-scroll .print-summary-row td {
              border-top: 1px solid #777 !important;
              border-bottom: 1px solid #777 !important;
            }

            .print-info-table,
            .print-info-table td {
              border: 1px solid #777 !important;
            }

            .wage-sheet-block,
            .wage-sheet-left,
            .wage-sheet-right {
              background: #ffffff !important;
            }

            .print-info-table,
            .wage-sheet-scroll .print-wage-table {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .wage-sheet-block {
              page-break-after: always;
              break-after: page;
            }

            .wage-sheet-block:last-child {
              page-break-after: auto;
              break-after: auto;
            }
          }
        `}
      </style>

      <Box className="wage-sheet-root">
        {sheets.map((sheet: any, sheetIndex: number) => {
          const options = {
            ...defaultOptions,
            ...(wageSheetData?.options || {}),
            ...(sheet?.options || {}),
          };

          const rows = sheet?.rows || [];
          const summary =
            sheet?.summary || {
              daysWorked: 0,
              outOfDays: 0,
              averageHours: 0,
              totalHours: 0,
              totalWage: 0,
              totalOvertime: 0,
              totalExtra: 0,
              totalDrive: 0,
              grandTotal: 0,
            };

          const staffName =
            sheet?.staff?.name || leadDetails?.lead?.customer_name || "";
          const staffAddress =
            sheet?.staff?.address || leadDetails?.lead?.address || "";
          const niNumber =
            sheet?.staff?.niNumber || leadDetails?.lead?.ni_number || "";
          const refNumber =
            sheet?.documentNumber || leadDetails?.lead?.lead_id || "";
          const printDate = sheet?.printDate || dayjs().format("DD-MM-YY");

          const showWage = options.showWage;
          const showOvertime = options.showOvertime;
          const showExtras = options.showExtras;
          const showTaskDetails = options.showTaskDetails;

          const columns = [
            { key: "day", label: "Day", width: "10%" },
            { key: "customer", label: "Customer", width: "25%" },
            { key: "notes", label: "Notes", width: "16%" },
            { key: "start", label: "Start", width: "7%", align: "center" },
            { key: "finish", label: "Finish", width: "7%", align: "center" },
            { key: "hours", label: "Hours", width: "7%", align: "right" },
            ...(showWage
              ? [{ key: "wage", label: "Wage", width: "7%", align: "right" }]
              : []),
            ...(showOvertime
              ? [{ key: "ot", label: "O/T", width: "7%", align: "right" }]
              : []),
            ...(showExtras
              ? [{ key: "extra", label: "Extra", width: "7%", align: "right" }]
              : []),
            { key: "drive", label: "Drive", width: "7%", align: "right" },
            { key: "total", label: "Total", width: "8%", align: "right" },
            ...(showTaskDetails
              ? [{ key: "taskDetails", label: "Task Details", width: "20%" }]
              : []),
          ];

          const summaryCells = [
            {
              key: "daysWorked",
              text: `Days\nWorked:${summary.daysWorked}`,
              align: "left",
            },
            { key: "outOf", text: `Out of:${summary.outOfDays}`, align: "center" },
            { key: "avgLabel", text: "Average Hours:", align: "right" },
            {
              key: "avgValue",
              text: formatCompact(summary.averageHours),
              align: "right",
            },
            { key: "totalsLabel", text: "Totals", align: "center" },
            {
              key: "totalHours",
              text: formatCompact(summary.totalHours),
              align: "right",
            },
            ...(showWage
              ? [
                  {
                    key: "totalWage",
                    text: formatCompact(summary.totalWage),
                    align: "right",
                  },
                ]
              : []),
            ...(showOvertime
              ? [
                  {
                    key: "totalOt",
                    text: formatCompact(summary.totalOvertime),
                    align: "right",
                  },
                ]
              : []),
            ...(showExtras
              ? [
                  {
                    key: "totalExtra",
                    text: formatCompact(summary.totalExtra),
                    align: "right",
                  },
                ]
              : []),
            {
              key: "totalDrive",
              text: formatCompact(summary.totalDrive),
              align: "right",
            },
            {
              key: "grand",
              text: formatPound(summary.grandTotal),
              align: "right",
            },
            ...(showTaskDetails ? [{ key: "spacer", text: "", align: "left" }] : []),
          ];

          return (
            <Box key={`${staffName}-${sheetIndex}`} className="wage-sheet-block">
              <Box className="wage-sheet-left">
                <table className="print-info-table" style={styles.infoTable}>
                  <tbody>
                    <tr>
                      <td style={styles.infoCell}>{staffName}</td>
                      <td style={{ ...styles.infoCell, textAlign: "right" }}>
                        Date: {printDate}
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.infoCell}>{staffAddress}</td>
                      <td style={{ ...styles.infoCell, textAlign: "right" }}>
                        Number: {refNumber}
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.infoCell}>NI Number: {niNumber}</td>
                      <td style={styles.infoCell}></td>
                    </tr>
                  </tbody>
                </table>
              </Box>

              <Box className="wage-sheet-right">
                <Box className="wage-sheet-scroll">
                  <table className="print-wage-table" style={styles.wageTable}>
                  <colgroup>
                    {columns.map((column) => (
                      <col key={column.key} style={{ width: column.width }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {columns.map((column) => (
                        <th
                          key={column.key}
                          style={{
                            ...styles.headerCell,
                            textAlign: column.align || "left",
                          }}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? (
                      rows.map((row: any, index: number) => (
                        <tr key={`${sheetIndex}-${row.day}-${index}`}>
                          <td style={{ ...styles.bodyCell, whiteSpace: "pre-line" }}>
                            {formatDayLabel(row)}
                          </td>
                          <td style={styles.bodyCell}>{row.customer || "-"}</td>
                          <td style={styles.bodyCell}>{row.notes || ""}</td>
                          <td style={{ ...styles.bodyCell, textAlign: "center" }}>
                            {row.start || ""}
                          </td>
                          <td style={{ ...styles.bodyCell, textAlign: "center" }}>
                            {row.finish || ""}
                          </td>
                          <td style={{ ...styles.bodyCell, textAlign: "right" }}>
                            {formatCompact(row.hours)}
                          </td>
                          {showWage && (
                            <td style={{ ...styles.bodyCell, textAlign: "right" }}>
                              {formatCompact(row.wage)}
                            </td>
                          )}
                          {showOvertime && (
                            <td style={{ ...styles.bodyCell, textAlign: "right" }}>
                              {formatTwo(row.overtime)}
                            </td>
                          )}
                          {showExtras && (
                            <td style={{ ...styles.bodyCell, textAlign: "right" }}>
                              {formatTwo(row.extra)}
                            </td>
                          )}
                          <td style={{ ...styles.bodyCell, textAlign: "right" }}>
                            {formatTwo(row.drive)}
                          </td>
                          <td
                            style={{
                              ...styles.bodyCell,
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {formatPound(row.total)}
                          </td>
                          {showTaskDetails && (
                            <td style={styles.bodyCell}>{row.taskDetails || ""}</td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{ ...styles.bodyCell, textAlign: "center" }}
                        >
                          No wage rows available
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tbody>
                    <tr className="print-summary-row" style={styles.summaryRow}>
                      {summaryCells.map((cell) => (
                        <td
                          key={cell.key}
                          style={{
                            ...styles.summaryCell,
                            textAlign: cell.align as any,
                            whiteSpace: "pre-line",
                          }}
                        >
                          {cell.text}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                  </table>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default WagesOffSheet;

const styles: any = {
  infoTable: {
    width: "100%",
    borderCollapse: "collapse",
    height: "100%",
    tableLayout: "fixed",
  },
  infoCell: {
    border: "1px solid #777",
    padding: "7px 8px",
    fontSize: "14px",
    color: "#6b6b6b",
    fontStyle: "italic",
    fontWeight: 700,
    lineHeight: 1.1,
    verticalAlign: "top",
  },
  wageTable: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  headerCell: {
    border: "1px solid #777",
    backgroundColor: "#b7b7b7",
    padding: "3px 6px",
    fontSize: "13px",
    fontWeight: 700,
    fontStyle: "italic",
    color: "#666",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    border: "1px solid #777",
    padding: "2px 6px",
    fontSize: "13px",
    color: "#666",
    fontStyle: "italic",
    fontWeight: 700,
    verticalAlign: "top",
  },
  summaryRow: {
    backgroundColor: "#b7b7b7",
  },
  summaryCell: {
    border: "1px solid #777",
    padding: "2px 6px",
    fontSize: "13px",
    fontWeight: 700,
    fontStyle: "italic",
    color: "#666",
    whiteSpace: "nowrap",
  },
};